import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mixHexColor, withAlpha } from './liveMonitorUtils';

export const Icon = ({ path, className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

export const LiveStatusBadge = ({ route }) => (
  <span
    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold"
    style={{
      color: route.statusColor,
      backgroundColor: route.statusSoftColor
    }}
  >
    <span
      className="w-2 h-2 rounded-full"
      style={{ backgroundColor: route.statusColor }}
    />
    {route.liveStatusLabel}
  </span>
);

export const LiveKpiCard = ({ title, value, helper, iconPath, tone = 'blue' }) => {
  const palette = {
    blue: {
      border: '#bfdbfe',
      tint: 'rgba(239,246,255,0.98)',
      topLine: '#3b82f6',
      iconBg: 'rgba(59,130,246,0.10)',
      iconBorder: 'rgba(59,130,246,0.18)',
      iconColor: '#2563eb',
      valueColor: '#0f172a',
      helperColor: '#64748b'
    },
    emerald: {
      border: '#a7f3d0',
      tint: 'rgba(236,253,245,0.98)',
      topLine: '#10b981',
      iconBg: 'rgba(16,185,129,0.10)',
      iconBorder: 'rgba(16,185,129,0.18)',
      iconColor: '#059669',
      valueColor: '#0f172a',
      helperColor: '#64748b'
    },
    amber: {
      border: '#fde68a',
      tint: 'rgba(255,251,235,0.98)',
      topLine: '#f59e0b',
      iconBg: 'rgba(245,158,11,0.10)',
      iconBorder: 'rgba(245,158,11,0.18)',
      iconColor: '#d97706',
      valueColor: '#0f172a',
      helperColor: '#64748b'
    },
    violet: {
      border: '#ddd6fe',
      tint: 'rgba(245,243,255,0.98)',
      topLine: '#8b5cf6',
      iconBg: 'rgba(139,92,246,0.10)',
      iconBorder: 'rgba(139,92,246,0.18)',
      iconColor: '#7c3aed',
      valueColor: '#0f172a',
      helperColor: '#64748b'
    }
  };

  const current = palette[tone] || palette.blue;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border px-3.5 py-3 shadow-sm"
      style={{
        borderColor: current.border,
        background: `linear-gradient(180deg, ${current.tint} 0%, rgba(255,255,255,0.98) 100%)`,
        boxShadow: '0 10px 28px -24px rgba(15,23,42,0.35)'
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: `linear-gradient(90deg, ${current.topLine} 0%, ${current.topLine}99 65%, transparent 100%)` }}
      />
      <div className="relative flex items-center gap-3">
        <div
          className="h-10 w-10 shrink-0 rounded-2xl border flex items-center justify-center"
          style={{
            backgroundColor: current.iconBg,
            borderColor: current.iconBorder,
            color: current.iconColor
          }}
        >
          <Icon path={iconPath} className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 truncate">{title}</p>
          <div className="mt-1 flex items-end justify-between gap-3">
            <p className="text-2xl leading-none font-black tracking-tight" style={{ color: current.valueColor }}>{value}</p>
            {helper ? <p className="text-[10px] leading-tight text-right max-w-[8rem]" style={{ color: current.helperColor }}>{helper}</p> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const DriverMonitorAvatar = ({ route, visual }) => {
  const photoURL = String(route?.driverPhotoURL || route?.route?.driverPhotoURL || '').trim();

  if (photoURL) {
    return (
      <div
        className="driver-photo-shell w-11 h-11 rounded-2xl border shrink-0 overflow-hidden"
        style={{
          borderColor: 'rgba(255,255,255,0.28)',
          backgroundColor: 'rgba(255,255,255,0.16)'
        }}
        title={route.transporterName}
      >
        <img
          src={photoURL}
          alt={route.transporterName || 'Chofer'}
          className="driver-photo-img h-full w-full object-cover"
        />
        <span className="driver-photo-glow" />
      </div>
    );
  }

  return (
    <div
      className="w-11 h-11 rounded-2xl border flex items-center justify-center shadow-sm shrink-0"
      style={{
        borderColor: 'rgba(255,255,255,0.28)',
        backgroundColor: 'rgba(255,255,255,0.16)'
      }}
      title={route.transportLabel}
    >
      <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="#ffffff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
        <path d={visual.path} />
      </svg>
    </div>
  );
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  en_camino: 'En camino',
  en_destino: 'En destino',
  completed: 'Completado',
  incomplete: 'Incompleto',
  cancelled: 'Cancelado'
};

const STATUS_STYLES = {
  pending: { bg: 'rgba(226,232,240,0.9)', color: '#475569' },
  en_camino: { bg: 'rgba(219,234,254,0.95)', color: '#2563eb' },
  en_destino: { bg: 'rgba(207,250,254,0.95)', color: '#0891b2' },
  completed: { bg: 'rgba(220,252,231,0.95)', color: '#16a34a' },
  incomplete: { bg: 'rgba(254,249,195,0.95)', color: '#ca8a04' },
  cancelled: { bg: 'rgba(254,226,226,0.95)', color: '#dc2626' }
};

const getDestinationTimestamp = (destination) => {
  const value =
    destination?.completedAt ||
    destination?.updatedAt ||
    destination?.timestamp ||
    null;

  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getManifestDestinations = (route, mode = 'plan') => {
  const destinations = Array.isArray(route?.destinations) ? [...route.destinations] : [];

  if (mode === 'plan') {
    return destinations.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  const statusPriority = {
    completed: 1,
    incomplete: 2,
    cancelled: 3,
    en_destino: 4,
    en_camino: 5,
    pending: 6
  };

  return destinations.sort((a, b) => {
    const priorityA = statusPriority[a.status] || 99;
    const priorityB = statusPriority[b.status] || 99;

    if (priorityA !== priorityB) return priorityA - priorityB;

    if (['completed', 'incomplete', 'cancelled'].includes(a.status) || ['completed', 'incomplete', 'cancelled'].includes(b.status)) {
      return getDestinationTimestamp(b) - getDestinationTimestamp(a);
    }

    return (a.order || 0) - (b.order || 0);
  });
};

const DestinationStatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.pending;

  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em]"
      style={{
        backgroundColor: style.bg,
        color: style.color
      }}
    >
      {STATUS_LABELS[status] || 'Pendiente'}
    </span>
  );
};

const RouteManifestInline = ({
  route,
  accentColor,
  accentSoftColor,
  onOpenRoute,
  containerId,
  openRouteButtonId
}) => {
  const [mode, setMode] = useState('plan');
  const manifestScrollRef = useRef(null);
  const touchStateRef = useRef({ startY: 0, startScrollTop: 0 });

  const items = useMemo(() => getManifestDestinations(route.route, mode), [route.route, mode]);

  const handleManifestWheel = (event) => {
    const scrollElement = manifestScrollRef.current;

    if (!scrollElement || scrollElement.scrollHeight <= scrollElement.clientHeight + 1) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    scrollElement.scrollTop += event.deltaY;
  };

  const handleManifestTouchStart = (event) => {
    const scrollElement = manifestScrollRef.current;
    const touch = event.touches?.[0];

    if (!scrollElement || !touch) {
      return;
    }

    touchStateRef.current = {
      startY: touch.clientY,
      startScrollTop: scrollElement.scrollTop
    };
  };

  const handleManifestTouchMove = (event) => {
    const scrollElement = manifestScrollRef.current;
    const touch = event.touches?.[0];

    if (!scrollElement || !touch || scrollElement.scrollHeight <= scrollElement.clientHeight + 1) {
      return;
    }

    const deltaY = touch.clientY - touchStateRef.current.startY;

    if (Math.abs(deltaY) < 3) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    scrollElement.scrollTop = touchStateRef.current.startScrollTop - deltaY;
  };

  const accentInk = mixHexColor(accentColor, '#0f172a', 0.34);
  const manifestShellColor = `linear-gradient(180deg, ${withAlpha(accentColor, 0.055)} 0%, ${withAlpha(accentColor, 0.024)} 100%)`;
  const manifestCardColor = withAlpha(accentColor, 0.045);
  const manifestCardBorder = withAlpha(accentColor, 0.14);
  const manifestNextColor = `linear-gradient(180deg, ${withAlpha(accentColor, 0.13)} 0%, ${withAlpha(accentColor, 0.075)} 100%)`;
  const manifestNextBorder = withAlpha(accentColor, 0.24);
  const manifestButtonBg = withAlpha(accentColor, 0.04);
  const manifestButtonActiveBg = withAlpha(accentColor, 0.14);

  return (
    <div
      id={containerId}
      className="rounded-2xl border p-3"
      style={{
        borderColor: withAlpha(accentColor, 0.18),
        background: manifestShellColor,
        boxShadow: `inset 0 1px 0 ${withAlpha('#ffffff', 0.68)}`,
        backdropFilter: 'blur(8px)'
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] font-black" style={{ color: accentColor }}>
            Hoja de ruta
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {mode === 'plan' ? 'Orden guardado de la ruta.' : 'Vista operativa según estado actual.'}
          </p>
        </div>

        <div
          className="inline-flex rounded-2xl border p-1"
          style={{
            borderColor: withAlpha(accentColor, 0.14),
            backgroundColor: manifestButtonBg,
            backdropFilter: 'blur(8px)'
          }}
        >
          {[
            { key: 'plan', label: 'Plan' },
            { key: 'operation', label: 'Operación' }
          ].map((option) => {
            const active = mode === option.key;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => setMode(option.key)}
                className="px-3 py-1.5 rounded-xl text-[11px] font-black transition-all"
                style={{
                  backgroundColor: active ? manifestButtonActiveBg : 'transparent',
                  color: active ? accentInk : '#475569',
                  border: active ? `1px solid ${withAlpha(accentColor, 0.18)}` : '1px solid transparent'
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        ref={manifestScrollRef}
        className="mt-3 min-h-0 max-h-[min(46vh,22rem)] overflow-y-auto overscroll-contain pr-1"
        onWheelCapture={handleManifestWheel}
        onTouchStartCapture={handleManifestTouchStart}
        onTouchMoveCapture={handleManifestTouchMove}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y'
        }}
      >
        <div className="space-y-2">
          {items.length === 0 ? (
            <div
              className="rounded-2xl border px-3 py-4 text-sm text-slate-500"
              style={{
                borderColor: manifestCardBorder,
                backgroundColor: withAlpha(accentColor, 0.04)
              }}
            >
              Esta ruta no tiene destinos cargados.
            </div>
          ) : (
            items.map((destination, index) => {
              const isNext =
                route?.nextDestination?.id &&
                destination?.id &&
                route.nextDestination.id === destination.id;

              return (
                <div
                  key={destination.id || `${destination.name}-${index}`}
                  className="rounded-2xl border px-3 py-3 transition-all"
                  style={{
                    borderColor: isNext ? manifestNextBorder : manifestCardBorder,
                    background: isNext ? manifestNextColor : manifestCardColor,
                    boxShadow: isNext ? `0 12px 26px -24px ${withAlpha(accentColor, 0.18)}` : 'none',
                    backdropFilter: 'blur(6px)'
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="inline-flex items-center justify-center min-w-[28px] h-7 rounded-xl px-2 text-[11px] font-black"
                          style={{
                            backgroundColor: withAlpha(accentColor, 0.12),
                            color: accentColor
                          }}
                        >
                          #{(destination.order || 0) + 1}
                        </span>

                        <p className="font-bold text-slate-800 truncate">
                          {destination.name || `Destino ${index + 1}`}
                        </p>

                        {isNext ? (
                          <span
                            className="inline-flex items-center rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em]"
                            style={{
                              backgroundColor: withAlpha(accentColor, 0.12),
                              color: accentColor
                            }}
                          >
                            Próxima
                          </span>
                        ) : null}
                      </div>

                      {destination.address ? (
                        <p className="mt-1 text-xs text-slate-500 line-clamp-2">
                          {destination.address}
                        </p>
                      ) : null}

                      <div className="mt-2">
                        <button
                          id={destination?.id === items[0]?.id ? openRouteButtonId : undefined}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenRoute?.(route.route, destination);
                          }}
                          className="px-3 py-1.5 rounded-xl text-[11px] font-black transition-opacity hover:opacity-90"
                          style={{
                            backgroundColor: withAlpha(accentColor, 0.12),
                            color: accentColor,
                            border: `1px solid ${accentColor}33`
                          }}
                        >
                          Abrir ruta
                        </button>
                      </div>
                    </div>

                    <DestinationStatusBadge status={destination.status} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export const RouteLiveListCard = ({
  route,
  selected,
  expanded,
  onSelect,
  onToggleExpand,
  onOpenDetail,
  onOpenRoute,
  openRouteButtonId,
  detailButtonId,
  manifestToggleButtonId,
  manifestContainerId,
  manifestOpenRouteButtonId,
  tutorialActive = false,
  tutorialFocusTarget = null
}) => {
  const visual = route.transportVisual;
  const accentColor = route.monitorAccentColor || visual.color;
  const accentSoftColor = route.monitorAccentSoftColor || visual.softColor;
  const accentDark = mixHexColor(accentColor, '#020617', 0.36);
  const accentGlow = withAlpha(accentColor, expanded ? 0.34 : selected ? 0.24 : 0.18);
  const buttonTextColor = expanded ? accentDark : '#ffffff';
  const [showManifest, setShowManifest] = useState(false);

  useEffect(() => {
    if (!tutorialActive) return;
    if (!expanded) return;

    const shouldOpenManifest = [
      manifestToggleButtonId ? `#${manifestToggleButtonId}` : null,
      manifestContainerId ? `#${manifestContainerId}` : null,
      manifestOpenRouteButtonId ? `#${manifestOpenRouteButtonId}` : null
    ].filter(Boolean).includes(tutorialFocusTarget);

    if (shouldOpenManifest) {
      setShowManifest(true);
    }
  }, [
    expanded,
    manifestContainerId,
    manifestOpenRouteButtonId,
    manifestToggleButtonId,
    tutorialActive,
    tutorialFocusTarget
  ]);

  const handleToggle = (event) => {
    event?.stopPropagation?.();
    onSelect(route.id);
    onToggleExpand(route.id);
  };

  const handleHeaderInteraction = () => {
    onSelect(route.id);
    onToggleExpand(route.id);
  };

  return (
    <div
      className="rounded-[1.35rem] border overflow-hidden transition-all duration-300"
      style={{
        borderColor: withAlpha(accentColor, expanded ? 0.9 : 0.64),
        boxShadow: expanded
          ? `0 24px 46px -28px ${accentGlow}`
          : selected
            ? `0 18px 38px -28px ${accentGlow}`
            : `0 12px 28px -26px rgba(15,23,42,0.24)`,
        backgroundColor: '#ffffff'
      }}
    >
      <div
        className="relative px-3 py-2.5 cursor-pointer transition-all duration-300 overflow-hidden shimmer"
        style={{
          background: `linear-gradient(135deg, ${mixHexColor(accentColor, '#ffffff', 0.08)} 0%, ${accentColor} 48%, ${accentDark} 100%)`,
          color: '#ffffff'
        }}
        onClick={handleHeaderInteraction}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleHeaderInteraction();
          }
        }}
      >
        {selected ? (
          <div className="pointer-events-none absolute inset-y-0 left-0 w-1.5 rounded-r-full bg-white/85" />
        ) : null}

        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(110deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 28%, rgba(255,255,255,0) 56%)'
          }}
        />

        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-32"
          style={{
            background: 'radial-gradient(circle at center, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 34%, rgba(255,255,255,0) 70%)',
            transform: 'translateX(26%)'
          }}
        />

        <div className="relative flex items-center gap-3">
          <DriverMonitorAvatar route={route} visual={visual} />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <h3 className="text-sm font-black text-white truncate tracking-[0.01em]">
                {route.transporterName}
              </h3>

              {route.gpsActive ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/12 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.16em] text-white/90">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-200/75 animate-ping"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-100"></span>
                  </span>
                  GPS activo
                </span>
              ) : null}
            </div>

            <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/72 truncate">
              {route.transportLabel}
            </p>
          </div>

          <button
            type="button"
            onClick={handleToggle}
            className="min-w-[94px] h-10 rounded-2xl border flex items-center justify-center gap-2 transition-all px-3 shadow-sm shrink-0"
            style={{
              borderColor: expanded ? withAlpha(accentColor, 0.22) : 'rgba(255,255,255,0.24)',
              backgroundColor: expanded ? '#ffffff' : 'rgba(255,255,255,0.14)',
              color: buttonTextColor,
              backdropFilter: 'blur(10px)'
            }}
            aria-label={expanded ? 'Contraer detalles' : 'Expandir detalles'}
            title={expanded ? 'Ocultar detalle' : 'Ver detalle'}
          >
            <span className="text-[10px] font-black uppercase tracking-[0.16em]">
              {expanded ? 'Ocultar' : 'Ver'}
            </span>
            <svg
              viewBox="0 0 24 24"
              width="17"
              height="17"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>
      </div>

      {expanded ? (
        <div
          className="px-4 pb-4 pt-3 border-t animate-fade-in space-y-3 text-sm bg-white"
          style={{ borderColor: `${accentColor}33` }}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-black text-white"
              style={{ backgroundColor: accentColor }}
            >
              {route.transportLabel}
            </span>

            <LiveStatusBadge route={route} />

            <span
              className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-black"
              style={{
                backgroundColor: accentSoftColor,
                color: accentColor,
                border: `1px solid ${accentColor}33`
              }}
            >
              ETA {route.etaLabel}
            </span>
          </div>

          <div
            className="rounded-2xl border px-3 py-2.5"
            style={{
              borderColor: `${accentColor}22`,
              backgroundColor: accentSoftColor
            }}
          >
            <p className="text-[11px] uppercase tracking-[0.16em] font-black" style={{ color: accentColor }}>
              Ruta activa
            </p>
            <p className="mt-1 font-bold text-slate-800">{route.name}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div
              className="rounded-2xl border p-3"
              style={{
                borderColor: `${accentColor}22`,
                backgroundColor: '#ffffff'
              }}
            >
              <p className="text-[11px] uppercase tracking-[0.16em] font-black" style={{ color: accentColor }}>
                Próxima parada
              </p>
              <p className="mt-1 font-bold text-slate-800">
                {route.nextDestination ? `#${(route.nextDestination.order || 0) + 1} ${route.nextDestination.name}` : 'Sin parada activa'}
              </p>
            </div>

            <div
              className="rounded-2xl border p-3"
              style={{
                borderColor: `${accentColor}22`,
                backgroundColor: '#ffffff'
              }}
            >
              <p className="text-[11px] uppercase tracking-[0.16em] font-black" style={{ color: accentColor }}>
                Última señal
              </p>
              <p className="mt-1 font-bold text-slate-800">{route.lastUpdateLabel}</p>
            </div>

            <div
              className="rounded-2xl border p-3"
              style={{
                borderColor: `${accentColor}22`,
                backgroundColor: '#ffffff'
              }}
            >
              <p className="text-[11px] uppercase tracking-[0.16em] font-black" style={{ color: accentColor }}>
                Velocidad
              </p>
              <p className="mt-1 font-bold text-slate-800">
                {Number.isFinite(route.observedSpeedKmh) && route.observedSpeedKmh > 0
                  ? `${route.observedSpeedKmh.toFixed(1)} km/h`
                  : 'Base operativa'}
              </p>
            </div>

            <div
              className="rounded-2xl border p-3"
              style={{
                borderColor: `${accentColor}22`,
                backgroundColor: '#ffffff'
              }}
            >
              <p className="text-[11px] uppercase tracking-[0.16em] font-black" style={{ color: accentColor }}>
                Recorrido
              </p>
              <p className="mt-1 font-bold text-slate-800">
                {route.completedDestinations}/{route.totalDestinations} destinos
              </p>
            </div>
          </div>

          <div
            className="rounded-2xl border p-3"
            style={{
              borderColor: `${accentColor}28`,
              backgroundColor: '#ffffff'
            }}
          >
            <div
              className="flex items-center justify-between gap-2 text-[11px] font-black uppercase tracking-[0.14em]"
              style={{ color: accentColor }}
            >
              <span>Progreso</span>
              <span className="text-slate-700">{route.progress}%</span>
            </div>

            <div
              className="mt-2 w-full rounded-full h-3 overflow-hidden"
              style={{ backgroundColor: `${accentColor}1f` }}
            >
              <div
                className="h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${route.progress}%`,
                  background: accentColor
                }}
              />
            </div>
          </div>

          <div
            className="flex items-center justify-between gap-3 text-sm rounded-2xl px-3.5 py-3"
            style={{ backgroundColor: accentSoftColor }}
          >
            <div>
              <p className="font-semibold text-slate-800">Mapa enfocado en esta unidad</p>
              <p className="text-slate-500 text-xs mt-1">
                Mientras esta barra esté abierta, el monitor acerca el zoom y resalta mejor su recorrido.
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
                <button
                  id={manifestToggleButtonId}
                  type="button"
                  onClick={(event) => {
                  event.stopPropagation();
                  setShowManifest((current) => !current);
                }}
                className="px-3.5 py-2.5 rounded-2xl text-xs font-black transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: '#ffffff',
                  color: accentColor,
                  border: `1px solid ${accentColor}33`
                }}
              >
                {showManifest ? 'Ocultar hoja' : 'Hoja de ruta'}
              </button>

                <button
                  id={detailButtonId || openRouteButtonId}
                  type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenDetail(route.route);
                }}
                className="px-3.5 py-2.5 rounded-2xl text-white text-xs font-black transition-opacity hover:opacity-90"
                style={{ backgroundColor: accentColor }}
              >
                Ver detalle
              </button>
            </div>
          </div>

          {showManifest ? (
              <RouteManifestInline
                route={route}
                accentColor={accentColor}
                accentSoftColor={accentSoftColor}
                onOpenRoute={onOpenRoute}
                containerId={manifestContainerId}
                openRouteButtonId={manifestOpenRouteButtonId}
              />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
